import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ASICard.css';
import { API_URL } from '../../Constant';


const ASICard = ({ handleCardClick, fullWidth = false, onDataChange, onDSLStatusChange, onYearChange }) => {
  const [asiYear, setAsiYear] = useState('');
  const [asiYearOptions, setAsiYearOptions] = useState([]);
  const [asiData, setAsiData] = useState(null);
  const [dslStatusSummary, setDslStatusSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Generate year options dynamically
  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11 (0 = January, 3 = April)
    
    // Financial year starts from April (month index 3)
    // If current month is before April, current financial year is (currentYear-1)-(currentYear)
    // If current month is April or after, current financial year is (currentYear)-(currentYear+1)
    const currentFinancialYearStart = currentMonth < 3 ? currentYear - 1 : currentYear;
    
    // Generate options up to but not including current financial year
    const options = [];
    for (let y = 2019; y < currentFinancialYearStart; y++) {
      options.push(`${y}-${y + 1}`);
    }
    const reversedOptions = options.reverse();
    setAsiYearOptions(reversedOptions);
    const initialYear = reversedOptions[0] || '2019-2020';
    setAsiYear(initialYear);
    // Notify parent component of initial year
    if (onYearChange) {
      onYearChange(initialYear);
    }
  }, []);

  // Fetch ASI data when year changes
  useEffect(() => {
    if (asiYear) {
      fetchASIData();
      fetchDSLStatusSummary();
      // Notify parent component of year change
      if (onYearChange) {
        onYearChange(asiYear);
      }
    }
  }, [asiYear]);

  const fetchASIData = async () => {
    setLoading(true);
    try {
      const yearParam = asiYear ? asiYear.split('-')[0] : '2022';
      const response = await axios.get(`${API_URL}api/totalCountDisDivGvadeshboard?year=${yearParam}`);
      if (response.data.status) {
        setAsiData(response.data.data);
        // Notify parent component of data change
        if (onDataChange) {
          onDataChange(response.data.data);
        }
      }
    } catch (error) {
      setAsiData(null);
      if (onDataChange) {
        onDataChange(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDSLStatusSummary = async () => {
    try {
      const yearParam = asiYear ? asiYear.split('-')[0] : '2022';
      const response = await axios.post(
        `${API_URL}api/asiDSLStatusSummary`,
        { survey_year: yearParam }
      );
      setDslStatusSummary(response.data);
      // Pass DSL status summary to parent component
      if (onDSLStatusChange) {
        onDSLStatusChange(response.data);
      }
    } catch (error) {
      setDslStatusSummary(null);
      if (onDSLStatusChange) {
        onDSLStatusChange(null);
      }
    }
  };

  // Add Indian currency formatting function (same as PriceWagesCard)
  const formatCurrency = (num) => num !== undefined && num !== null && num !== '' ? `${Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '0.00';

  const cardStyle = {
    transition: 'all 0.3s ease',
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    borderLeft: '4px solid #ff8c00'
  };

  const gradientText = {
    background: 'linear-gradient(135deg, #ff8c00 0%, #ff4500 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  };

  return (
    <div className={fullWidth ? "col-12 mb-4" : "col-xl-6 col-lg-6 mb-4"}>
      <div className="card h-100" style={cardStyle}>
        <div className="card-body">
          <div className="d-flex align-items-center mb-3 justify-content-between">
            <div className="d-flex align-items-center">
              <div className="fs-1 me-3" style={{ animation: 'pulse 2s infinite' }}>📊</div>
              <div>
                <h5 className="card-title mb-0" style={gradientText}>Annual Survey of Industries</h5>
                <p className="text-muted mb-0 small">ASI Data Management</p>
              </div>
            </div>
            {/* Year Dropdown */}
            <div className="ms-3" style={{ minWidth: 180 }}>
              <label htmlFor="asi-year-select" className="form-label fw-bold mb-0 me-2">Year:</label>
              <select
                id="asi-year-select"
                className="form-select form-select-sm d-inline-block"
                value={asiYear}
                onChange={e => setAsiYear(e.target.value)}
                style={{ width: 120, height: 35, display: 'inline-block' }}
              >
                {asiYearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center">Loading ASI data...</div>
          ) : (
            <>
              {/* Stats Section */}
              <div className="row mb-3 d-flex justify-content-between">
                <div className="col text-center">
                  <div className="fw-bold" style={{ color: '#ff8c00' }}>
                    {asiData?.getTotalcountlastYearbase?.[0]?.total_dsl_count || "—"}
                  </div>
                  <small className="text-muted">Total DSL</small>
                </div>
                <div className="col text-center">
                  <div className="fw-bold" style={{ color: '#ff6b35' }}>
                    {dslStatusSummary?.totalPending ?? "—"}
                  </div>
                  <small className="text-muted">Pending DSL</small>
                </div>
                <div className="col text-center">
                  <div className="fw-bold" style={{ color: '#ff4500' }}>
                    {dslStatusSummary?.totalSubmitted ?? "—"}
                  </div>
                  <small className="text-muted">Submitted DSL</small>
                </div>
                <div className="col text-center">
                  <div className="fw-bold" style={{ color: '#28a745' }}>
                    {dslStatusSummary?.totalAccepted ?? "—"}
                  </div>
                  <small className="text-muted">Accepted DSL</small>
                </div>
                <div className="col text-center">
                  <div className="fw-bold" style={{ color: '#dc3545' }}>
                    {dslStatusSummary?.totalRejected ?? "—"}
                  </div>
                  <small className="text-muted">Rejected DSL</small>
                </div>
              </div>

              {/* ASI Details Tables */}
              {asiData?.getTop5andBottom5 && (
                <div>
                  <div className="mt-2">
                    <b>Top 5 GVA/NVA:</b>
                    <div className="asi-table-responsive">
                      <table className="table table-bordered table-sm mt-1 asi-table-fixed-cols">
                        <thead>
                          <tr>
                            <th className="asi-table-col-dsl">DSL No</th>
                            <th className="asi-table-col-division">Division</th>
                            <th className="asi-table-col-district">District</th>
                            <th className="asi-table-col-gva">GVA in (₹) </th>
                            <th className="asi-table-col-nva">NVA in (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {asiData.getTop5andBottom5.slice(0, 5).map((item, idx) => (
                            <tr key={idx}>
                              <td className="asi-table-col-dsl">{
                                (item.DSL_NO || item.dsl_no)
                                  ? Number(item.DSL_NO || item.dsl_no).toFixed(0)
                                  : '00'
                              }</td>
                              <td className="asi-table-col-division">
                                {(item.DIVISION || item.DIVISION_NAME || item.division || '').replace(/\s+Div(?:i|s)?sion\s*$/i, '').trim()}</td>
                              <td className="asi-table-col-district">{item.DISTRICT || item.DISTRICT_NAME || item.district || ''}</td>
                              <td className="asi-table-col-gva">{formatCurrency(item.GVA)}</td>
                              <td className="asi-table-col-nva">{formatCurrency(item.NVA)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="mt-2">
                    <b>Bottom 5 GVA/NVA:</b>
                    <div className="asi-table-responsive">
                      <table className="table table-bordered table-sm mt-1 asi-table-fixed-cols">
                        <thead>
                          <tr>
                            <th className="asi-table-col-dsl">DSL No</th>
                            <th className="asi-table-col-division">Division</th>
                            <th className="asi-table-col-district">District</th>
                            <th className="asi-table-col-gva">GVA in (₹)</th>
                            <th className="asi-table-col-nva">NVA in (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {asiData.getTop5andBottom5.slice(-5).map((item, idx) => (
                            <tr key={idx}>
                              <td className="asi-table-col-dsl">{
                                (item.DSL_NO || item.dsl_no)
                                  ? Number(item.DSL_NO || item.dsl_no).toFixed(0)
                                  : '00'
                              }</td>
                              <td className="asi-table-col-division">
                                {(item.DIVISION || item.DIVISION_NAME || item.division || '').replace(/\s+Div(?:i|s)?sion\s*$/i, '').trim()}
                              </td>
                              <td className="asi-table-col-district">{item.DISTRICT || item.DISTRICT_NAME || item.district || ''}</td>
                              <td className="asi-table-col-gva">{formatCurrency(item.GVA)}</td>
                              <td className="asi-table-col-nva">{formatCurrency(item.NVA)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ASICard; 
